import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  deleteCallsExportJob,
  downloadCallsExportJob,
  listCallsExportJobs,
  startCallsExport,
} from '../../api';
import { toast } from '../../hooks/use-toast';

const ExportJobsContext = createContext(null);
const ACTIVE_STATUSES = new Set(['queued', 'running']);

const DEFAULT_EXPORT_API = {
  listJobs: listCallsExportJobs,
  startExport: startCallsExport,
  downloadJob: downloadCallsExportJob,
  deleteJob: deleteCallsExportJob,
};

const isActiveJob = (job) => ACTIVE_STATUSES.has(String(job?.status ?? '').toLowerCase());

export const ExportJobsProvider = ({ children, api = DEFAULT_EXPORT_API }) => {
  const [jobs, setJobs] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingParams, setPendingParams] = useState(null);

  const activeCount = useMemo(() => jobs.filter(isActiveJob).length, [jobs]);

  const refreshJobs = useCallback(async () => {
    try {
      const response = await api.listJobs();
      setJobs(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error(err);
    }
  }, [api]);

  const doStartExport = useCallback(async (params, force) => {
    try {
      await api.startExport(params, force);
      setIsPanelOpen(true);
      await refreshJobs();
      toast({ title: 'Выгрузка поставлена в очередь' });
    } catch (err) {
      if (err?.response?.status === 409 && !force) {
        setPendingParams(params);
        setConfirmOpen(true);
        setIsPanelOpen(true);
        await refreshJobs();
        return;
      }

      toast({
        title: 'Не удалось запустить выгрузку',
        description: err?.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
    }
  }, [api, refreshJobs]);

  const startExport = useCallback((params) => doStartExport(params, false), [doStartExport]);

  const confirmStartExport = useCallback(async () => {
    const params = pendingParams;
    setConfirmOpen(false);
    setPendingParams(null);

    if (params) {
      await doStartExport(params, true);
    }
  }, [doStartExport, pendingParams]);

  const downloadJob = useCallback(async (job) => {
    try {
      await api.downloadJob(job.id, job.filename);
    } catch (err) {
      console.error(err);
      toast({
        title: 'Не удалось скачать файл',
        description: err?.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
    }
  }, [api]);

  const deleteJob = useCallback(async (id) => {
    try {
      await api.deleteJob(id);
      await refreshJobs();
    } catch (err) {
      console.error(err);
      toast({
        title: 'Не удалось удалить выгрузку',
        description: err?.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
    }
  }, [api, refreshJobs]);

  useEffect(() => {
    refreshJobs();
  }, [refreshJobs]);

  useEffect(() => {
    const interval = window.setInterval(refreshJobs, activeCount > 0 ? 3000 : 10000);
    return () => window.clearInterval(interval);
  }, [activeCount, refreshJobs]);

  const value = useMemo(() => ({
    jobs,
    activeCount,
    isPanelOpen,
    setIsPanelOpen,
    refreshJobs,
    startExport,
    downloadJob,
    deleteJob,
  }), [activeCount, deleteJob, downloadJob, isPanelOpen, jobs, refreshJobs, startExport]);

  return (
    <ExportJobsContext.Provider value={value}>
      {children}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Запустить еще одну выгрузку?</AlertDialogTitle>
            <AlertDialogDescription>
              У вас уже есть активная выгрузка. Новый отчет будет поставлен в очередь и выполнится параллельно с учетом лимитов сервера.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingParams(null)}>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStartExport}>Запустить</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ExportJobsContext.Provider>
  );
};

export const useExportJobs = () => {
  const context = useContext(ExportJobsContext);
  if (!context) {
    throw new Error('useExportJobs must be used within ExportJobsProvider');
  }

  return context;
};

export { isActiveJob };
